import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Comentario } from "model/solicitacao/comentario/Comentario";
import { CriacaoComentarioDTO } from "model/solicitacao/comentario/CriacaoComentarioDTO";
import { ApiComentarios } from "service/api/solicitacao/ApiComentario";
import { RootState } from "store/store";
import { ErrosUtils } from "util/ErrosUtils";
import { MarkdownUtil } from "util/markdown/MarkdownUtil";
import { ComentarioCamposFormulario } from "view/section/SolicitacaoComentariosSecao/NovoComentarioTag/NovoComentarioCamposFormulario";


type CriacaoComentarioType = {
  idSolicitacao: number,
  formulario: ComentarioCamposFormulario
}

export class NovoComentarioAction {
  static readonly iniciar = createAction('ComentariosSolicitacao/criacao/iniciar');
  static readonly cancelar = createAction('ComentariosSolicitacao/criacao/cancelar');
  
  static readonly persistir = (idSolicitacao: number, formulario: ComentarioCamposFormulario) => {
    return NovoComentarioAction._persistir({idSolicitacao: idSolicitacao, formulario: formulario})
  }

  static readonly _persistir = createAsyncThunk(
    'ComentariosSolicitacao/criacao/persistir',
    async (criacao: CriacaoComentarioType, {rejectWithValue}) => {
      const mensagem = criacao.formulario.comentario.mensagem?.trim() ?? ""
      const comentarioMarkdown = MarkdownUtil.toMarkdown(mensagem);
      
      const anexosAplicaveis = criacao.formulario.comentario.anexos
        .map(it => it.serverId)
        .filter(it => it !== undefined && it !== null)

      const comentario: CriacaoComentarioDTO = {
        mensagem: comentarioMarkdown,
        idsAnexos: anexosAplicaveis
      }

      try {
        return await ApiComentarios.persistirNovoComentario(criacao.idSolicitacao, comentario);
      } catch (e) {
        return rejectWithValue(ErrosUtils.converterErroAxiosParaErroSistema(e));
      }
    }
  )
}

export type TipoAcaoListaSolicitacao = 
  | 'carregarPrimeiraPagina'
  | 'carregarPaginaSeguinte'
  | 'tentarCarregarNovamentePaginaAtual'
  ;

type ListaSolicitacaoType = {
  idSolicitacao: number;
  acao: TipoAcaoListaSolicitacao;
}

class ListaComentariosAction {
  static readonly iniciar = (idSolicitacao: number) => {
    return ListaComentariosAction._carregar({idSolicitacao: idSolicitacao, acao: 'carregarPrimeiraPagina'});
  }

  static readonly carregarNovamentePaginaAtual = (idSolicitacao: number) => {
    return ListaComentariosAction._carregar({idSolicitacao: idSolicitacao, acao: 'tentarCarregarNovamentePaginaAtual'});
  }

  static readonly carregarProximaPagina = (idSolicitacao: number) => {
    return ListaComentariosAction._carregar({idSolicitacao: idSolicitacao, acao: 'carregarPaginaSeguinte'});
  }
  
  static readonly adicionarNaLista = createAction<Comentario>('ComentariosSolicitacao/lista/adicionarNaLista');

  static readonly _carregar = createAsyncThunk(
    'ComentariosSolicitacao/lista/carregar',
    async ({idSolicitacao, acao}: ListaSolicitacaoType, {getState, rejectWithValue}) => {
      const { comentariosSolicitacao } = getState() as RootState;

      // ComentariosSolicitacaoAction.lista._carregar.pending define a página atual como a próxima
      // por isso não precisa fazer += 1;
      const paginaASerCarregada = comentariosSolicitacao.comentarios.pagina.atual;
      const proximaPagina = acao === 'carregarPrimeiraPagina'
        ? 1
        : paginaASerCarregada;

      try {
        return await ApiComentarios.getComentarios(idSolicitacao, proximaPagina);
      } catch (e) {
        return rejectWithValue(ErrosUtils.converterErroAxiosParaErroSistema(e));
      }
    }
  ) 
}


export class ComentariosSolicitacaoAction {
  public static readonly criacao = NovoComentarioAction;
  public static readonly lista = ListaComentariosAction
}